import React, { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Lightbox({ images = [], index, onClose, onChange, alt = "" }) {
  const open = index !== null && index >= 0;

  const prev = useCallback(
    (e) => { e && e.stopPropagation(); onChange((index - 1 + images.length) % images.length); },
    [index, images.length, onChange]
  );
  const next = useCallback(
    (e) => { e && e.stopPropagation(); onChange((index + 1) % images.length); },
    [index, images.length, onChange]
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [open, onClose, prev, next]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="court-lightbox"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
          className="fixed inset-0 z-[80] bg-[#04120b]/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
        >
          <button
            data-testid="lightbox-close"
            onClick={onClose}
            className="absolute top-5 right-5 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-sc-lime hover:text-[#06170f] text-white flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>

          <button
            data-testid="lightbox-prev"
            onClick={prev}
            className="absolute left-3 sm:left-6 z-10 w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-white/10 hover:bg-sc-lime hover:text-[#06170f] text-white flex items-center justify-center transition-colors"
            aria-label="Previous"
          >
            <ChevronLeft className="w-6 h-6 sm:w-7 sm:h-7" />
          </button>

          <AnimatePresence mode="wait">
            <motion.img
              key={index}
              src={images[index]}
              alt={alt}
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.3, ease: [0.2, 0.7, 0.2, 1] }}
              className="max-h-[85vh] max-w-[92vw] object-contain rounded-xl shadow-2xl select-none"
            />
          </AnimatePresence>

          <button
            data-testid="lightbox-next"
            onClick={next}
            className="absolute right-3 sm:right-6 z-10 w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-white/10 hover:bg-sc-lime hover:text-[#06170f] text-white flex items-center justify-center transition-colors"
            aria-label="Next"
          >
            <ChevronRight className="w-6 h-6 sm:w-7 sm:h-7" />
          </button>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono2 text-xs tracking-widest text-white/60">
            {index + 1} / {images.length}
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
